import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, X, Sparkles, Crown, Loader2, Zap, Ticket } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [checkingStripe, setCheckingStripe] = useState(true);

  // Check if Stripe is configured on mount
  useEffect(() => {
    const checkStripeStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/stripe-status`);
        setStripeEnabled(response.data.enabled);
      } catch (error) {
        console.error('Failed to check Stripe status:', error);
        setStripeEnabled(false);
      } finally {
        setCheckingStripe(false);
      }
    };
    checkStripeStatus();
  }, []);

  const plans = [
    {
      name: 'Free',
      price: 'Free',
      description: 'Test the waters with limited access',
      icon: Sparkles,
      iconColor: 'text-slate-600',
      gradient: 'from-slate-100 to-slate-50',
      buttonText: 'Start Free',
      buttonVariant: 'outline',
      features: [
        { text: '1 WordPress domain', included: true },
        { text: '7 days dashboard data', included: true },
        { text: '50 posts fetch limit', included: true },
        { text: '3 deletes per day', included: true },
        { text: 'Basic filtering', included: true },
        { text: 'Column sorting', included: false },
        { text: 'Full dashboard data', included: false },
        { text: 'Priority support', included: false },
      ]
    },
    {
      name: 'Professional',
      price: '$9.99',
      period: '/month',
      description: 'For serious content management',
      icon: Crown,
      iconColor: 'text-purple-600',
      gradient: 'from-purple-100 via-cyan-50 to-purple-100',
      buttonText: 'Upgrade to Pro',
      buttonVariant: 'default',
      popular: true,
      planType: 'pro',
      features: [
        { text: '9 WordPress domains', included: true },
        { text: 'All dashboard data', included: true },
        { text: 'Unlimited posts fetch', included: true },
        { text: '50 deletes per day', included: true },
        { text: 'Column sorting', included: true },
        { text: 'Full analytics access', included: true },
        { text: 'Priority support', included: true },
      ]
    },
    {
      name: 'Lifetime Deal',
      price: '$99',
      period: ' one-time',
      description: 'Pay once, use forever',
      icon: Zap,
      iconColor: 'text-amber-600',
      gradient: 'from-amber-100 via-yellow-50 to-amber-100',
      buttonText: 'Get Lifetime Access',
      buttonVariant: 'default',
      lifetime: true,
      planType: 'lifetime',
      features: [
        { text: '9 WordPress domains', included: true },
        { text: 'All dashboard data', included: true },
        { text: 'Unlimited posts fetch', included: true },
        { text: '50 deletes per day', included: true },
        { text: 'Column sorting', included: true },
        { text: 'Priority support forever', included: true },
        { text: 'Lifetime updates', included: true },
        { text: 'Early access to features', included: true },
      ]
    }
  ];

  const handlePlanSelect = async (plan) => {
    // Free plan - just navigate to signup/login
    if (!plan.popular && !plan.lifetime) {
      if (token) {
        navigate('/app/posts');
      } else {
        navigate('/signup');
      }
      return;
    }

    // Check if Stripe is enabled
    if (!stripeEnabled) {
      toast.error('Payments are not configured. Please contact the administrator.');
      return;
    }

    // For paid plans, need to be logged in
    if (!token) {
      const planParam = plan.lifetime ? 'lifetime' : 'pro';
      toast.info('Sign up to get your account');
      navigate(`/signup?plan=${planParam}`);
      return;
    }

    // Initiate Stripe checkout for both Pro and Lifetime
    const packageId = plan.lifetime ? 'lifetime' : 'pro_monthly';
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        window.location.assign(response.data.url);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to initiate checkout';
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (!token) {
      toast.info('Please sign up or log in first');
      navigate('/signup?plan=lifetime');
      return;
    }

    setApplyingCoupon(true);
    try {
      const response = await axios.post(
        `${API}/coupons/apply`,
        { code: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setCouponCode('');
      // Redirect to account page to see new plan
      setTimeout(() => navigate('/app/account'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-cyan-50 py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 mb-6 text-sm font-mono uppercase tracking-widest text-purple-600 border border-purple-300 rounded-full bg-purple-50">
            Simple Pricing
          </span>
          <h1 className="text-5xl md:text-6xl font-heading font-bold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Choose Your Plan
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Start free to test the tool, upgrade when you're ready to clean up thousands of posts
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      ⭐ Most Popular
                    </span>
                  </div>
                )}
                {plan.lifetime && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      🔥 Limited Time
                    </span>
                  </div>
                )}
                
                <Card className={`relative overflow-hidden p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 ${
                  plan.popular ? 'border-purple-300 bg-gradient-to-br ' + plan.gradient : 
                  plan.lifetime ? 'border-amber-300 bg-gradient-to-br ' + plan.gradient : 
                  'border-slate-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${
                      plan.popular ? 'from-purple-600 to-cyan-600' : 
                      plan.lifetime ? 'from-amber-500 to-yellow-500' : 
                      'from-slate-200 to-slate-100'
                    }`}>
                      <Icon className={`w-8 h-8 ${plan.popular ? 'text-white' : plan.lifetime ? 'text-black' : plan.iconColor}`} />
                    </div>
                    {plan.popular && (
                      <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                        Best Value
                      </div>
                    )}
                    {plan.lifetime && (
                      <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold">
                        Pay Once
                      </div>
                    )}
                  </div>

                  <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-slate-600 mb-6">{plan.description}</p>

                  <div className="mb-8">
                    <span className="text-5xl font-heading font-black bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-slate-600 ml-2">{plan.period}</span>
                    )}
                  </div>

                  <Button
                    onClick={() => handlePlanSelect(plan)}
                    disabled={loading || checkingStripe || (user?.subscription?.plan === 'pro' && plan.popular) || ((plan.popular || plan.lifetime) && !stripeEnabled)}
                    className={`w-full h-12 rounded-xl font-semibold transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50'
                    } ${((plan.popular || plan.lifetime) && !stripeEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading && plan.popular ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : checkingStripe && (plan.popular || plan.lifetime) ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : user?.subscription?.plan === 'pro' && plan.popular ? (
                      'Current Plan'
                    ) : (plan.popular || plan.lifetime) && !stripeEnabled ? (
                      'Coming Soon'
                    ) : (
                      plan.buttonText
                    )}
                  </Button>

                  <div className="mt-8 space-y-4">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`text-sm ${
                          feature.included ? 'text-slate-700' : 'text-slate-400 line-through'
                        }`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Coupon Code Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-xl mx-auto mt-16"
        >
          <Card className="p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Ticket className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading font-bold text-slate-900">Have a Lifetime Deal Coupon?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Enter your coupon code to unlock lifetime access instantly
            </p>
            <div className="flex gap-3">
              <Input
                placeholder="Enter code (e.g., LIFE-XXXX-XXXX)"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1 font-mono bg-white"
              />
              <Button 
                onClick={applyCoupon}
                disabled={applyingCoupon}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold"
              >
                {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* FAQ or Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-slate-600 mb-4">
            All plans include WordPress & GA4 integration, secure encryption, and 24/7 uptime
          </p>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-purple-600 hover:text-purple-700"
          >
            ← Back to Home
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
